import {
  type MutateOptions,
  useMutation,
  useQueryClient,
} from '@tanstack/react-query';
import { gql } from 'graphql-request';
import { usePageExperienceContext } from '@/features/experiences/contexts/PageExperienceContext';
import { gqlClient } from '@/lib';
import type { QueryError } from '@/utils/errors';
import {
  createExperienceQueryKey,
  createExperienceSlugQueryKey,
} from '@/utils/queryKeys';
import type {
  UpdateExperienceSlugMutation,
  UpdateExperienceSlugMutationVariables,
} from './useUpdateExperienceSlug.generated';

const query = gql`
  mutation UpdateExperienceSlug($input: UpdateExperienceSlugInput!) {
    updateExperienceSlug(input: $input)
  }
`;

export const useUpdateExperienceSlug = () => {
  const queryClient = useQueryClient();
  const { experience } = usePageExperienceContext();
  const experienceId = String(experience?.id);

  const { mutate, ...rest } = useMutation<
    UpdateExperienceSlugMutation,
    QueryError,
    UpdateExperienceSlugMutationVariables
  >({
    mutationFn: (variables) => {
      return gqlClient.request<
        UpdateExperienceSlugMutation,
        UpdateExperienceSlugMutationVariables
      >(query, variables);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: createExperienceSlugQueryKey(experienceId),
      });
      queryClient.refetchQueries({
        queryKey: createExperienceQueryKey(experienceId),
      });
    },
  });

  const updateSlug = (
    slug: string,
    options?: MutateOptions<
      UpdateExperienceSlugMutation,
      QueryError,
      UpdateExperienceSlugMutationVariables
    >
  ) =>
    mutate(
      { input: { experienceId, slug } },
      {
        ...options,
        onSuccess: (...args) => {
          queryClient.invalidateQueries({
            queryKey: createExperienceSlugQueryKey(experienceId),
          });
          queryClient.refetchQueries({
            queryKey: createExperienceQueryKey(experienceId),
          });
          options?.onSuccess?.(...args);
        },
      }
    );

  return {
    updateSlug,
    ...rest,
  };
};
